import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CategoryCard } from "@/components/CategoryCard";
import { HeroSearch } from "@/components/HeroSearch";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, ArrowRight, ArrowUpRight, Star, CheckCircle2, Clock, Sparkles,
  TrendingUp, Users, Award, Quote, Activity, Lock, FileCheck, Zap
} from "lucide-react";

const Home = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchCats = async (retries = 2) => {
      try {
        const r = await api.get("/service-categories");
        if (!cancelled) setCategories(r.data);
      } catch (err) {
        if (retries > 0 && !cancelled) setTimeout(() => fetchCats(retries - 1), 800);
      }
    };
    fetchCats();
    return () => { cancelled = true; };
  }, []);

  const totalServices = categories.reduce((acc, c) => acc + c.subcategories.length, 0);

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFCFB]">
      <Header />

      {/* ═══════════════ HERO — SEM FIRST ═══════════════ */}
      <section className="relative bc-grain overflow-x-clip">
        {/* Ambient layered blobs */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-1/2 -translate-y-1/2 -right-32 w-[640px] h-[640px] rounded-full bg-[#F5A623]/12 blur-3xl bc-breath" />
          <div className="absolute bottom-0 -left-32 w-[520px] h-[520px] rounded-full bg-[#0A1128]/6 blur-3xl" />
          {/* Subtle grid lines */}
          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage: "linear-gradient(to right, #0A1128 1px, transparent 1px), linear-gradient(to bottom, #0A1128 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-14 md:pb-24">
          <div className="grid lg:grid-cols-[1.35fr_1fr] gap-10 lg:gap-16 items-start">
            {/* LEFT */}
            <div>
              <div className="bc-fade-up">
                <span className="pill-amber" data-testid="hero-badge">
                  <Sparkles size={11} className="mr-1" /> {t("home.hero.badge")}
                </span>
              </div>
              <h1 className="bc-fade-up bc-fade-up-d1 font-heading text-[44px] sm:text-[60px] lg:text-[80px] font-medium text-[#0A1128] mt-5 leading-[1.02] tracking-tight">
                {t("home.hero.title_1")} {t("home.hero.title_2")}{" "}
                <span className="text-[#F5A623] whitespace-nowrap">{t("home.hero.highlight")}</span>{" "}
                {t("home.hero.title_3")}
              </h1>
              <p className="bc-fade-up bc-fade-up-d2 mt-6 text-base md:text-lg text-[#0A1128]/65 max-w-xl leading-relaxed">
                {t("home.hero.subtitle")}
              </p>

              <div className="bc-fade-up bc-fade-up-d3 mt-8 relative z-30">
                <HeroSearch />
              </div>

              {/* Trust row */}
              <div className="bc-fade-up bc-fade-up-d4 mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 relative z-10" data-testid="hero-trust-row">
                {[
                  { icon: ShieldCheck, label: t("home.hero.trust_refund"), color: "text-[#16A34A]" },
                  { icon: CheckCircle2, label: t("home.hero.trust_no_cure"), color: "text-[#16A34A]" },
                  { icon: Clock, label: t("home.hero.minute"), color: "text-[#F5A623]" },
                  { icon: ShieldCheck, label: t("home.hero.trust_verified"), color: "text-[#0A1128]" },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-start gap-2 text-[12px] font-medium text-[#0A1128]/75">
                      <Icon size={15} className={`${item.color} flex-shrink-0 mt-0.5`} />
                      <span className="leading-tight">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT — Editorial SEM preview card */}
            <div className="bc-fade-up bc-fade-up-d2 relative lg:pt-8">
              <div className="relative">
                {/* Decorative outline behind */}
                <div className="hidden lg:block absolute -inset-4 border border-[#EAE6DF] rounded-md -z-10" />
                <div className="hidden lg:block absolute -inset-8 border border-[#EAE6DF]/50 rounded-md -z-10" />

                <div className="bg-white border border-[#EAE6DF] rounded-md p-5 md:p-6 shadow-[0_24px_64px_rgba(10,17,40,0.08)]">
                  {/* SEM header */}
                  <div className="flex items-center gap-3 pb-4 border-b border-[#EAE6DF]">
                    <div className="relative">
                      <div className="w-11 h-11 rounded-md bg-[#0A1128] text-[#F5A623] flex items-center justify-center font-heading font-extrabold text-[11px] tracking-wider">
                        SEM
                      </div>
                      <span className="absolute inset-0 rounded-md bc-pulse-ring pointer-events-none" />
                    </div>
                    <div className="flex-1">
                      <div className="font-heading font-medium text-[#0A1128] leading-tight">SEM</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#16A34A] font-bold flex items-center gap-1 mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] bc-live-dot" /> {t("sem.online")}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0A1128]/40">AI</span>
                  </div>

                  {/* Mock conversation */}
                  <div className="mt-5 space-y-3 text-[13px]">
                    <div className="bg-[#F3F0EA]/60 border border-[#EAE6DF] rounded-md rounded-tl-sm px-3.5 py-2.5 leading-relaxed text-[#0A1128]/80">
                      {t("home.sem.preview_q1")}
                    </div>
                    <div className="bg-[#F5A623]/12 border border-[#F5A623]/30 rounded-md rounded-tr-sm px-3.5 py-2.5 leading-relaxed text-[#0A1128] ml-6">
                      {t("home.sem.preview_a1")}
                    </div>
                    {/* Suggested service card mock */}
                    <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-white border border-[#EAE6DF] rounded-md ml-6 hover:border-[#0A1128]/30 transition-colors">
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-[#0A1128] truncate">{t("home.specialisms.vat")}</div>
                        <div className="text-[10px] text-[#0A1128]/50 uppercase tracking-[0.14em]">{t("services.cat.financieel", "Financieel")}</div>
                      </div>
                      <ArrowRight size={14} className="text-[#F5A623] flex-shrink-0" />
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-[#0A1128]/40 pl-1">
                      <span className="inline-flex gap-1">
                        <span className="w-1 h-1 rounded-full bg-[#0A1128]/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1 h-1 rounded-full bg-[#0A1128]/40 animate-bounce" style={{ animationDelay: "120ms" }} />
                        <span className="w-1 h-1 rounded-full bg-[#0A1128]/40 animate-bounce" style={{ animationDelay: "240ms" }} />
                      </span>
                      {t("home.sem.preview_typing")}
                    </div>
                  </div>

                  <Link
                    to="/sem"
                    className="mt-5 w-full btn-amber !rounded-md group justify-center"
                    data-testid="hero-sem-card-cta"
                  >
                    <Sparkles size={14} /> {t("home.sem.cta")}
                    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>

                {/* Floating mini stat card */}
                <div className="hidden md:flex absolute -bottom-6 -left-6 bg-[#0A1128] text-white rounded-md px-4 py-3 shadow-[0_16px_48px_rgba(10,17,40,0.24)] items-center gap-3 border border-white/10">
                  <div className="w-9 h-9 rounded-md bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
                    <Activity size={16} />
                  </div>
                  <div>
                    <div className="font-heading font-medium text-base tabular flex items-center gap-2">
                      12
                      <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] bc-live-dot" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/55">{t("home.stats.matches_today")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ LIVE ACTIVITY TICKER ═══════════════ */}
      <section className="bg-[#0A1128] text-white border-y border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-6">
          <span className="hidden md:flex items-center gap-2 eyebrow !text-[#F5A623] flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] bc-live-dot" />
            {t("home.ticker.live", "Live")}
          </span>
          <div className="flex-1 overflow-hidden">
            <div className="flex gap-12 bc-marquee whitespace-nowrap">
              {[...Array(2)].flatMap((_, i) => [
                t("home.ticker.t1", "ZZP'er uit Amsterdam koppelde aan boekhouder"),
                t("home.ticker.t2", "Webshop uit Rotterdam vond cybersecurity-partner"),
                t("home.ticker.t3", "Bouwbedrijf uit Eindhoven matchte advocaat"),
                t("home.ticker.t4", "Startup uit Utrecht vond marketing-bureau"),
                t("home.ticker.t5", "Particulier kreeg notaris binnen 24u"),
                t("home.ticker.t6", "Restaurant uit Den Haag koppelde verzekering"),
              ].map((m, j) => (
                <span key={`${i}-${j}`} className="text-[13px] text-white/75 inline-flex items-center gap-2">
                  <Sparkles size={11} className="text-[#F5A623]" /> {m}
                </span>
              )))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ STATS STRIP ═══════════════ */}
      <section className="bg-[#FDFCFB] border-b border-[#EAE6DF]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10" data-testid="home-stats">
            {[
              { v: "12", l: t("home.stats.matches_today"), icon: TrendingUp, live: true },
              { v: "200+", l: t("home.stats.providers"), icon: Users },
              { v: "4.9", suffix: "/5", l: t("home.stats.rating"), icon: Star },
              { v: "100%", l: t("home.stats.refund"), icon: Award },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.l} className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-md border border-[#EAE6DF] bg-white text-[#0A1128] flex items-center justify-center flex-shrink-0">
                    <Icon size={18} strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading text-3xl md:text-4xl font-medium text-[#0A1128] leading-none tracking-tight tabular flex items-baseline gap-1">
                      {s.v}
                      {s.suffix && <span className="text-base text-[#0A1128]/40 font-normal">{s.suffix}</span>}
                      {s.live && <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] bc-live-dot ml-1" />}
                    </div>
                    <div className="eyebrow mt-2">{s.l}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════ 9-CATEGORY BENTO ═══════════════ */}
      <section className="bg-[#F3F0EA]/40 border-y border-[#EAE6DF]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-end mb-12">
            <div>
              <span className="eyebrow">{t("home.services.badge")}</span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-[56px] font-medium text-[#0A1128] mt-4 tracking-tight leading-[1.05]">
                {t("home.services.title")}
              </h2>
            </div>
            <p className="text-[#0A1128]/60 text-base md:text-lg leading-relaxed lg:pb-2">
              {t("home.services.subtitle_count", { count: totalServices })}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {categories.map((c, i) => (
              <div key={c.id} className="bc-fade-up" style={{ animationDelay: `${0.05 + i * 0.04}s` }}>
                <CategoryCard category={c} />
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/diensten" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] hover:text-[#F5A623] transition-colors group" data-testid="home-all-services-link">
              {t("services.view_all")}
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════ SEM AI — FULL BAND ═══════════════ */}
      <section className="bg-[#0A1128] text-white relative overflow-hidden bc-grain">
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#F5A623]/12 blur-3xl pointer-events-none bc-breath" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#1A2B4A] blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 relative">
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F5A623]/12 text-[#F5A623] text-[10px] font-bold uppercase tracking-[0.22em] border border-[#F5A623]/25">
                <Sparkles size={11} /> {t("home.sem.badge")}
              </span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-[64px] font-medium mt-5 tracking-tight leading-[1.02]">
                {t("home.sem.title_1")} <span className="text-[#F5A623]">SEM</span> {t("home.sem.title_2")}
              </h2>
              <p className="text-white/65 mt-6 text-base md:text-lg max-w-xl leading-relaxed">
                {t("home.sem.subtitle")}
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-3">
                {[t("home.sem.b1"), t("home.sem.b2"), t("home.sem.b3")].map((b, i) => (
                  <div key={b} className="bg-white/5 border border-white/10 rounded-md p-4">
                    <div className="text-[#F5A623] font-heading font-medium text-lg tabular">0{i + 1}</div>
                    <p className="text-[12px] text-white/75 mt-2 leading-relaxed">{b}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/sem" className="btn-amber-lg group" data-testid="home-sem-cta">
                  <Sparkles size={16} /> {t("home.sem.cta")}
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link to="/onboarding" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-white/25 text-white hover:bg-white hover:text-[#0A1128] font-semibold transition-colors" data-testid="home-sem-skip">
                  {t("home.sem.skip")} <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Live SEM chat shell */}
            <div className="relative">
              <div className="bg-white rounded-md p-6 text-[#0A1128] shadow-[0_32px_96px_rgba(10,17,40,0.4)] border border-white/20">
                <div className="flex items-center gap-3 pb-4 border-b border-[#EAE6DF]">
                  <div className="relative">
                    <div className="w-11 h-11 rounded-md bg-[#0A1128] text-[#F5A623] flex items-center justify-center font-heading font-extrabold text-[11px] tracking-wider">SEM</div>
                    <span className="absolute inset-0 rounded-md bc-pulse-ring pointer-events-none" />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-medium leading-tight">SEM</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#16A34A] font-bold flex items-center gap-1 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] bc-live-dot" /> {t("sem.online")}
                    </div>
                  </div>
                </div>
                <div className="mt-4 space-y-3 text-[13px]">
                  <div className="bg-[#F3F0EA]/60 border border-[#EAE6DF] rounded-md rounded-tl-sm px-3.5 py-2.5 leading-relaxed text-[#0A1128]/80">
                    {t("home.sem.preview_q1")}
                  </div>
                  <div className="bg-[#F5A623]/12 border border-[#F5A623]/30 rounded-md rounded-tr-sm px-3.5 py-2.5 leading-relaxed text-[#0A1128] ml-6">
                    {t("home.sem.preview_a1")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ HOW IT WORKS ═══════════════ */}
      <section className="bg-[#FDFCFB]" id="zo-werkt-het">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 lg:gap-16 items-start mb-12">
            <div>
              <span className="eyebrow">{t("home.how.badge")}</span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-[56px] font-medium text-[#0A1128] mt-4 tracking-tight leading-[1.05]">
                {t("home.how.title")}
              </h2>
            </div>
            <div className="lg:pt-6 text-[#0A1128]/60 leading-relaxed">
              <p>{t("home.how.subtitle", "Een rechte lijn van vraag naar de juiste expert. Geen formulier-jungle. Geen verkooppraatjes.")}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#EAE6DF] border border-[#EAE6DF] rounded-md overflow-hidden">
            {[
              { n: "01", t: t("home.how.step_1_title"), d: t("home.how.step_1_desc") },
              { n: "02", t: t("home.how.step_2_title"), d: t("home.how.step_2_desc") },
              { n: "03", t: t("home.how.step_3_title"), d: t("home.how.step_3_desc") },
              { n: "04", t: t("home.how.step_4_title"), d: t("home.how.step_4_desc") },
            ].map((s) => (
              <div key={s.n} className="bg-white p-7 md:p-8 hover:bg-[#F3F0EA]/40 transition-colors group cursor-default">
                <div className="text-[#F5A623] font-heading font-medium text-2xl tabular">{s.n}</div>
                <h3 className="font-heading font-medium text-lg text-[#0A1128] mt-4 tracking-tight">{s.t}</h3>
                <p className="text-[13px] text-[#0A1128]/60 mt-3 leading-relaxed">{s.d}</p>
                <ArrowRight size={16} className="text-[#0A1128]/20 mt-6 transition-all group-hover:text-[#F5A623] group-hover:translate-x-1" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ TRUST / SAFE-MATCH BAND ═══════════════ */}
      <section className="bg-[#F3F0EA]/40 border-y border-[#EAE6DF]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="grid md:grid-cols-4 gap-8 md:gap-10">
            {[
              { icon: ShieldCheck, t: t("home.trust.refund_t", "€49 terug bij match"), d: t("home.trust.refund_d", "Volledige terugbetaling zodra je een match kiest. Geen kleine lettertjes.") },
              { icon: Lock, t: t("home.trust.security_t", "AVG & versleuteld"), d: t("home.trust.security_d", "Je gegevens zijn end-to-end versleuteld en blijven binnen de EU.") },
              { icon: FileCheck, t: t("home.trust.verified_t", "KvK-geverifieerd"), d: t("home.trust.verified_d", "Elke dienstverlener wordt handmatig gescreend voor toelating.") },
              { icon: Zap, t: t("home.trust.speed_t", "Match binnen 24u"), d: t("home.trust.speed_d", "Gemiddeld heb je binnen één werkdag 2–3 voorstellen.") },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.t} className="border-l border-[#0A1128]/15 pl-5">
                  <Icon size={20} className="text-[#F5A623]" strokeWidth={1.75} />
                  <h3 className="font-heading font-medium text-lg text-[#0A1128] mt-4 tracking-tight">{item.t}</h3>
                  <p className="text-[13px] text-[#0A1128]/60 mt-2 leading-relaxed">{item.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════ TESTIMONIALS ═══════════════ */}
      <section className="bg-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 lg:gap-16 items-end mb-12">
            <div>
              <span className="eyebrow">{t("home.testimonials.badge")}</span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-[56px] font-medium text-[#0A1128] mt-4 tracking-tight leading-[1.05]">
                {t("home.testimonials.title")}
              </h2>
            </div>
            <div className="lg:pt-6 flex items-center gap-3">
              <div className="flex">
                {[1,2,3,4,5].map((s) => <Star key={s} size={18} className="fill-[#F5A623] text-[#F5A623]" />)}
              </div>
              <span className="text-[#0A1128]/60 text-sm">
                <span className="font-heading font-medium text-[#0A1128] text-base">4.9</span> {t("home.hero.reviews_count")}
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5 md:gap-6" data-testid="home-testimonials">
            {[
              { q: t("home.testimonials.t1_quote"), n: t("home.testimonials.t1_name"), r: t("home.testimonials.t1_role"), c: "MV" },
              { q: t("home.testimonials.t2_quote"), n: t("home.testimonials.t2_name"), r: t("home.testimonials.t2_role"), c: "JK" },
              { q: t("home.testimonials.t3_quote"), n: t("home.testimonials.t3_name"), r: t("home.testimonials.t3_role"), c: "SD" },
            ].map((item, i) => (
              <figure
                key={item.n}
                className={`bg-white border border-[#EAE6DF] rounded-md p-7 md:p-8 flex flex-col hover:border-[#0A1128]/20 transition-colors ${i === 1 ? "lg:translate-y-6" : ""}`}
              >
                <Quote size={28} className="text-[#F5A623]" strokeWidth={1.5} />
                <blockquote className="text-[#0A1128]/80 mt-5 leading-relaxed flex-1 text-[15px]">
                  "{item.q}"
                </blockquote>
                <figcaption className="flex items-center gap-3 mt-7 pt-5 border-t border-[#EAE6DF]">
                  <div className="w-10 h-10 rounded-md bg-[#0A1128] text-white flex items-center justify-center font-heading font-medium text-[12px] tracking-wider">{item.c}</div>
                  <div className="flex-1">
                    <div className="font-heading font-medium text-[#0A1128] text-sm">{item.n}</div>
                    <div className="text-[11px] text-[#0A1128]/55 uppercase tracking-[0.12em] mt-0.5">{item.r}</div>
                  </div>
                  <div className="flex">
                    {[1,2,3,4,5].map((s) => <Star key={s} size={11} className="fill-[#F5A623] text-[#F5A623]" />)}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
